import { ListFundingsResponse } from '@cbinsights/fundingservice/fundingservice';
import { DealExtraction } from '../apis/GetDealQueue';

// The funding (or funding draft) a deal's new information should be merged INTO, instead of creating a
// new funding draft. `idFunding > 0` means a published funding (or an edit-draft of one); a target with
// `idFunding === 0` is a brand-new draft that hasn't been published yet.
export interface UpdateTarget {
  idCbiEntity: number; // whose funding page to open
  idFunding: number;
  idFundingDraft: string; // '' when the target is a published funding
  label: string;
}

// What to bring over from the deal. Amount, date and round are always opt-in: they're rarely the point of
// an update and silently overwriting them would be risky.
export interface UpdateMergeOptions {
  mentions: boolean; // the deal's article(s) -- every article in its cluster
  investors: boolean; // the deal's investors not already on the funding
  amount: boolean;
  date: boolean;
  round: boolean;
}

export const DEFAULT_MERGE_OPTIONS: UpdateMergeOptions = {
  mentions: true,
  investors: true,
  amount: false,
  date: false,
  round: false,
};

export const updateTargetKey = (target: UpdateTarget): string =>
  target.idFundingDraft
    ? `draft:${target.idFundingDraft}`
    : `funding:${target.idFunding}`;

// "Series A, 10M, 2026-01-15" -- whichever parts are known.
export const describeFunding = (parts: {
  round?: string;
  amount?: number | null;
  date?: string;
}): string =>
  [
    parts.round,
    parts.amount != null && parts.amount > 0 ? `${parts.amount}M` : '',
    parts.date,
  ]
    .filter(Boolean)
    .join(', ');

const withDetail = (title: string, detail: string): string =>
  detail ? `${title} · ${detail}` : title;

// The targets already known from the card's flags, best candidate first: the funding the deal is already
// linked to, then later-published fundings that may be the same deal, then active drafts.
export function buildSuggestedTargets(
  anchor: DealExtraction,
  idCbiEntity: number
): UpdateTarget[] {
  const targets: UpdateTarget[] = [];

  if (anchor.publishedFunding) {
    targets.push({
      idCbiEntity,
      idFunding: anchor.publishedFunding.idFunding,
      idFundingDraft: '',
      label: `Funding #${anchor.publishedFunding.idFunding} · already linked`,
    });
  }

  anchor.potentialDuplicates.forEach((dup) => {
    targets.push({
      idCbiEntity,
      idFunding: dup.idFunding,
      idFundingDraft: '',
      label: withDetail(
        `Funding #${dup.idFunding}`,
        describeFunding({
          round: dup.round,
          amount: dup.amount,
          date: dup.fundingDate,
        })
      ),
    });
  });

  anchor.potentialDuplicateDrafts.forEach((draft) => {
    targets.push({
      idCbiEntity,
      idFunding: draft.idFunding,
      idFundingDraft: draft.idFundingDraft,
      label: withDetail(
        draft.idFunding > 0
          ? `Draft edit of funding #${draft.idFunding}`
          : 'New draft',
        describeFunding({
          round: draft.round,
          amount: draft.amount,
          date: draft.fundingDate,
        })
      ),
    });
  });

  // The same funding/draft can be suggested twice (e.g. linked AND listed as a possible duplicate).
  const seen = new Set<string>();
  return targets.filter((target) => {
    const key = updateTargetKey(target);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

// Targets for the "Other…" picker: an entity's published fundings and its not-yet-published drafts.
// Edit-drafts of a published funding are skipped -- picking the published funding already resolves to
// its edit-draft on the funding page.
export function fundingsToTargets(
  response: ListFundingsResponse | undefined,
  idCbiEntity: number
): UpdateTarget[] {
  const published: UpdateTarget[] = (response?.fundingInfo || []).map(
    (funding) => ({
      idCbiEntity,
      idFunding: funding.idFunding,
      idFundingDraft: '',
      label: withDetail(
        `Funding #${funding.idFunding}`,
        describeFunding({
          round: funding.fundingRound?.name,
          amount: funding.amount,
          date: (funding.fundingDate || '').slice(0, 10),
        })
      ),
    })
  );

  const drafts: UpdateTarget[] = (response?.fundingDraftInfo || [])
    .filter((draft) => !draft.idFunding)
    .map((draft) => ({
      idCbiEntity,
      idFunding: 0,
      idFundingDraft: draft.idFundingDraft,
      label: withDetail(
        'New draft',
        describeFunding({
          round: draft.fundingRound?.name,
          amount: draft.amount,
          date: (draft.fundingDate || '').slice(0, 10),
        })
      ),
    }));

  return [...published, ...drafts];
}

// Whether the target is still there to be updated (a draft may have been discarded or published since).
export function updateTargetExists(
  response: ListFundingsResponse | undefined,
  target: UpdateTarget
): boolean {
  if (!response) {
    return true; // not loaded yet -- don't report a false "not found"
  }
  if (target.idFundingDraft) {
    return (response.fundingDraftInfo || []).some(
      (draft) => draft.idFundingDraft === target.idFundingDraft
    );
  }
  return (response.fundingInfo || []).some(
    (funding) => funding.idFunding === target.idFunding
  );
}

// The id (draft id, else funding id) of the row an Update merges into -- the one to scroll to and highlight.
// A published funding that already has an edit-draft is merged into that edit-draft, so its draft id wins;
// highlighting the published row would point at the wrong one.
export function resolveFocusId(
  response: ListFundingsResponse | undefined,
  target: UpdateTarget
): string {
  if (target.idFundingDraft) {
    return target.idFundingDraft;
  }
  const editDraft = (response?.fundingDraftInfo || []).find(
    (draft) => draft.idFunding === target.idFunding
  );
  return editDraft?.idFundingDraft || String(target.idFunding);
}
